import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import CPPDocumentsInput from 'components/Patient/CPP/CPPInput/Inputs/CPPDocumentsInput';
import ErrorBoundary from 'components/Utils/ErrorBoundary';
import RouteUtils from 'services/RouteUtils';
import CPPUtils from 'services/CPPUtils';
import './style.scss';
import NewPrescription from 'components/HealthRecords/Prescriptions/NewPrescription';
import NewReferral from 'components/HealthRecords/Referrals/NewReferral';
import NewImmunization from 'components/HealthRecords/Immunizations/NewImmunization';
import DocumentTemplateFavorites from 'components/HealthRecords/Documents/DocumentTemplate/DocumentTemplateFavorites';
import AppointmentNoteScreen from 'components/HealthRecords/AppointmentNotes/AppointmentNoteScreen';
import CPPSectionInput from 'components/Patient/CPP/CPPInput/Inputs/CPPSectionInput';
import { SECTION_CONFIGS } from 'components/Patient/CPP/CPPInput/Inputs/CPPSectionInput/configs';
import DefaultModal from 'components/Utils/DefaultModal';
import CPPPatientMedicationInput from 'components/Patient/CPP/CPPInput/Inputs/CPPPatientMedicationInput';
import { useAppSelector } from 'careand-redux/store';
import NewAdministeredDrug from 'components/HealthRecords/Immunizations/NewAdministeredDrug';
import { NavStack, usePathParams } from 'components/Navigation/NavStack';
import { useNavStack } from 'careand-redux/slices/ui/hooks';
import { Stacks } from 'careand-redux/slices/ui/navigation';
import { PhysicianPhysicianTypeEnum } from 'generated/api';
import { uiActions } from 'careand-redux/slices/ui/ui';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useProactiveCarey } from 'hooks/useProactiveCarey';
import config from 'config';

const CPPInput = ({ patient, bubble, activeCPPSections, setActiveCPPTab, closeCPPSection }) => {
  const authentication = useAppSelector((state) => state.authentication);
  const params = usePathParams();
  const nav = useNavStack(Stacks.Master);
  const activeTab = params.tab;

  const iconListClassNames = 'row icon-list shadow-300';

  const onCloseNote = useCallback(() => {
    let route = RouteUtils.getCPPRoute(patient.id, undefined, bubble);
    nav.push(route);
  }, [patient.id, bubble, nav]);

  // Determine which icon is active: check sections set first, then fall back to route-based tab
  const activeIcon = useMemo(() => {
    if (activeCPPSections.size > 0) {
      return [...activeCPPSections].pop();
    }
    return activeTab;
  }, [activeCPPSections, activeTab]);

  return (
    <div className="cpp-input-wrapper">
      <ErrorBoundary>
        <NoteArea patient={patient} bubble={bubble} onClose={onCloseNote} />
        <SectionArea
          activeCPPSections={activeCPPSections}
          patient={patient}
          bubble={bubble}
          closeCPPSection={closeCPPSection}
        />
      </ErrorBoundary>
      <div className={iconListClassNames}>
        <Icons
          patient={patient}
          activeTab={activeIcon}
          iconClick={setActiveCPPTab}
          authentication={authentication}
        />
      </div>
    </div>
  );
};

const Icons = React.memo(({ patient, activeTab, iconClick, authentication }) => {
  const dispatch = useAppDispatch();
  let role = authentication.user.physician_type || PhysicianPhysicianTypeEnum.NursePractitioner;

  return Object.keys(ICONS[role]).map((tab) => {
    let active = tab === activeTab;

    const info = ICONS[role][tab];

    return (
      <li key={tab}>
        <Icon
          height="25px"
          src={info.src}
          alt={info.alt}
          showTooltip={true}
          active={active}
          onClick={info.onClick ? () => info.onClick(dispatch, patient.id) : () => iconClick(tab)}
          id={`icon-${tab}`}
        />
        {active ? <div className="marker" /> : null}
      </li>
    );
  });
});

const NoteArea = ({ patient, bubble, onClose }) => {
  const { start, stop, runOnce } = useProactiveCarey(patient.id, bubble?.appointment_id);
  const activeUserId = useAppSelector((state) => state.authentication.activeUserId);

  useEffect(() => {
    const allow =
      config.ENVIRONMENT === 'staging' ||
      activeUserId === '2f564813-3c61-49c3-96e4-71761795a65e' ||
      activeUserId === '8063e17a-71e8-43b9-8181-414fa5d9a6e3' ||
      activeUserId === '53a3af20-0842-11e8-b499-2d06ff911a34';
    if (allow) start();
    return () => {
      if (allow) stop();
    };
  }, [start, stop]);

  return (
    <NavStack
      stack="Master"
      authenticateAll
      routes={{
        '/:tab/:id/note/:elementId?': {
          element: (
            <div className="input-area large">
              <AppointmentNoteScreen
                patient={patient}
                onClose={onClose}
                bubble={bubble}
                askProactiveCarey={runOnce}
              />
            </div>
          ),
        },
      }}
    />
  );
};

const SectionArea = ({ activeCPPSections, patient, bubble, closeCPPSection }) => {
  if (activeCPPSections.size === 0) return null;

  return [...activeCPPSections].map((section) => (
    <SingleSection
      key={section}
      section={section}
      patient={patient}
      bubble={bubble}
      onClose={() => closeCPPSection(section)}
    />
  ));
};

const SingleSection = ({ section, patient, bubble, onClose }) => {
  switch (section) {
    case 'documents':
      return (
        <div className="input-area large">
          <CPPDocumentsInput bubble={bubble} onClose={onClose} patient={patient} />
        </div>
      );
    case 'notes':
      return <SectionModal config={SECTION_CONFIGS.notes} patient={patient} onClose={onClose} />;
    case 'immunizations':
      return (
        <div className="input-area">
          <div className="cpp-input cpp-immunization-input">
            <NewImmunization
              patient={patient}
              form={CPPUtils.getFormName('immunizations', patient.id)}
              onClose={onClose}
            />
          </div>
        </div>
      );
    case 'administered_drugs':
      return (
        <div className="input-area">
          <div className="cpp-input cpp-administered-drugs-input">
            <NewAdministeredDrug
              patient={patient}
              form={CPPUtils.getFormName('administered_drugs', patient.id)}
              onClose={onClose}
            />
          </div>
        </div>
      );
    case 'referral':
      return (
        <div className="input-area large">
          <div className="cpp-input cpp-referral-input">
            <NewReferral
              patient={patient}
              form={CPPUtils.getFormName('referral', patient.id)}
              onClose={onClose}
            />
          </div>
        </div>
      );
    case 'prescription':
      return (
        <div className="input-area">
          <div className="cpp-input cpp-prescription-input">
            <NewPrescription
              patient={patient}
              form={CPPUtils.getFormName('prescription', patient.id)}
              onClose={onClose}
              onSave={onClose}
            />
          </div>
        </div>
      );
    case 'active_issues':
      return (
        <SectionModal config={SECTION_CONFIGS.active_issues} patient={patient} onClose={onClose} />
      );
    case 'medical_issues':
      return (
        <SectionModal config={SECTION_CONFIGS.medical_issues} patient={patient} onClose={onClose} />
      );
    case 'preventative_screenings':
      return (
        <SectionModal
          config={SECTION_CONFIGS.preventative_screenings}
          patient={patient}
          onClose={onClose}
        />
      );
    case 'allergies':
      return (
        <SectionModal config={SECTION_CONFIGS.allergies} patient={patient} onClose={onClose} />
      );
    case 'medications':
      return (
        <DefaultModal
          title="Medication"
          className="modal cpp-section-modal cpp-medication-modal animated fadeIn"
          wrap={true}
          padding={true}
          content={
            <CPPPatientMedicationInput
              key="medications"
              title="Medication"
              field="medications"
              patient={patient}
            />
          }
          isOpen={true}
          onRequestClose={onClose}
          draggable={true}
        />
      );
    case 'history/family':
      return (
        <SectionModal config={SECTION_CONFIGS.family_history} patient={patient} onClose={onClose} />
      );
    case 'history/surgical':
      return (
        <SectionModal
          config={SECTION_CONFIGS.surgical_history}
          patient={patient}
          onClose={onClose}
        />
      );
    case 'history/ethnicity':
      return (
        <SectionModal
          config={SECTION_CONFIGS.ethnicity_history}
          patient={patient}
          onClose={onClose}
        />
      );
    case 'lifestyle_factors':
      return (
        <SectionModal
          config={SECTION_CONFIGS.lifestyle_factors}
          patient={patient}
          onClose={onClose}
        />
      );
    case 'consultants':
      return (
        <SectionModal config={SECTION_CONFIGS.consultants} patient={patient} onClose={onClose} />
      );
    case 'favorites':
      return (
        <div className="input-area large">
          <div className="cpp-input cpp-favorite-input">
            <DocumentTemplateFavorites patient={patient} onClose={onClose} />
          </div>
        </div>
      );
    default:
      return null;
  }
};

CPPInput.propTypes = {
  patient: PropTypes.object.isRequired,
  bubble: PropTypes.object,
  activeCPPSections: PropTypes.instanceOf(Set),
  setActiveCPPTab: PropTypes.func,
  closeCPPSection: PropTypes.func,
};

export default CPPInput;

const ICONS = {
  [PhysicianPhysicianTypeEnum.Phlebotomist]: {
    note: { src: 'memo-pad' },
    requisition: {
      src: 'chart-line',
      alt: 'Requisitions & Referrals & Resources',
      onClick: (dispatch, patientId) => openReqsList(dispatch, patientId),
    },
    referral: { src: 'user-doctor' },
  },
  [PhysicianPhysicianTypeEnum.Nurse]: {
    note: { src: 'memo-pad' },
    requisition: {
      src: 'chart-line',
      alt: 'Requisitions & Referrals & Resources',
      onClick: (dispatch, patientId) => openReqsList(dispatch, patientId),
    },
    referral: { src: 'user-doctor' },
    favorites: { src: 'star' },
  },
  [PhysicianPhysicianTypeEnum.NursePractitioner]: {
    note: { src: 'memo-pad' },
    prescription: { src: 'prescription' },
    requisition: {
      src: 'chart-line',
      alt: 'Requisitions & Referrals & Resources',
      onClick: (dispatch, patientId) => openReqsList(dispatch, patientId),
    },
    referral: { src: 'user-doctor' },
    favorites: { src: 'star' },
  },
};

const SectionModal = ({ config, patient, onClose }) => (
  <DefaultModal
    title={config.title}
    className="modal cpp-section-modal animated fadeIn"
    wrap={true}
    padding={true}
    content={<CPPSectionInput config={config} patient={patient} />}
    isOpen={true}
    onRequestClose={onClose}
    draggable={true}
  />
);

function openReqsList(dispatch, patientId) {
  dispatch(uiActions.addPinnedDocument({ patientId, type: 'reqs_list', id: 'reqs_list' }));
}
